import { useState } from "react";
import { Control, FieldValues, Path } from "react-hook-form";
import { useDebounce } from "use-debounce";

import { Command, CommandItem, CommandList } from "@/components/ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useGetPlaces } from "@/hooks/queries/useGetPlaces";

export const LocationInput = <T extends FieldValues>({
  fieldName,
  formControl,
  inputClassName,
}: {
  fieldName: Path<T>;
  formControl: Control<T>;
  inputClassName?: string;
}) => {
  const [locationString, setLocationString] = useState<string>("");
  const [showPlacesList, setShowPlacesList] = useState<boolean>(false);
  const [debouncedLocationString] = useDebounce(locationString, 250);

  const { data: placesData } = useGetPlaces(debouncedLocationString);
  const places: string[] = placesData ?? [];

  return (
    <FormField
      control={formControl}
      name={fieldName}
      render={({ field }) => (
        <FormItem className="w-full text-slate-700">
          <FormLabel>Location</FormLabel>
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                className={inputClassName}
                // Add delay to allow `CommandItem` `onSelect` below to fire off.
                onBlur={() => {
                  setTimeout(() => {
                    setShowPlacesList(false);
                  }, 150);
                }}
                onChange={(e) => {
                  const placesQuery = e.target.value;
                  setShowPlacesList(placesQuery.length > 0);
                  setLocationString(placesQuery);
                  field.onChange(placesQuery);
                }}
                placeholder="Begin typing in a location..."
              />
              {showPlacesList && places.length > 0 && (
                <Command>
                  <CommandList className="absolute z-10 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                    {places.map((place: string) => (
                      <CommandItem
                        key={place}
                        className="cursor-pointer px-3 py-2 hover:bg-gray-100"
                        onSelect={() => {
                          setShowPlacesList(false);
                          setLocationString(place);
                          field.onChange(place);
                        }}
                      >
                        {place}
                      </CommandItem>
                    ))}
                  </CommandList>
                </Command>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
